/**
 * Der Zeichen-Vorrat der App — EINE Quelle für alle gezeichneten Symbole.
 *
 * Entstanden am 25.08. als `zuhauseZeichen()` in zuhause.ts, für die Zeilen
 * auf der Startseite. Am 26.08. hierher gezogen und erweitert, weil dieselben
 * Zeichen an fünf weiteren Stellen gebraucht wurden — und weil sonst genau
 * das passiert wäre, was in diesem Blatt schon dreimal passiert ist: eine
 * zweite Kopie, die auseinanderläuft.
 *
 * ACHTUNG: KEINE EMOJI. Sie sehen auf jedem Gerät anders aus, tragen eine fremde
 * Farbigkeit ins warme Papier und lassen sich nicht einfärben. Ein
 * Strich-Zeichen nimmt die Textfarbe an und trägt jede Palette mit.
 *
 * Alle auf 24er-Raster, Strichstärke 1.6, `currentColor`, runde Enden —
 * ein einziger Stil, damit nichts nebeneinander fremd wirkt.
 */

/**
 * Dach, Waende, Tuer. Steht fuer zwei Dinge, deshalb EIN Pfad und zwei Namen:
 * die Kontakt-Kategorie „Familie" und der Weg zurueck nach `haus`. Zwei
 * gleiche Zeichnungen nebeneinander waeren eine Kopie zu viel.
 */
const HAUS =
  '<path d="M3.6 10.4 12 3.8l8.4 6.6"/><path d="M5.6 9v10.4h12.8V9"/><path d="M9.8 19.4v-5.2h4.4v5.2"/>';

/** Die Pfade. Ein Eintrag = ein Zeichen. */
const PFADE: Record<string, string> = {
  // ── Zuhause-Zeilen (seit 25.08.)
  /** Offener Faden — wartet auf Antwort. */
  faden:
    '<path d="M5 4.6h11.5a2.5 2.5 0 0 1 2.5 2.5V20l-3-2.2H5a1.6 1.6 0 0 1-1.6-1.6V6.2A1.6 1.6 0 0 1 5 4.6Z"/>',
  mail: '<rect x="3.4" y="5.6" width="17.2" height="12.8" rx="2.2"/><path d="m3.8 7 8.2 5.6L20.2 7"/>',
  /** Uhr — etwas liegt und wartet. */
  erinnerung: '<circle cx="12" cy="12" r="8.6"/><path d="M12 7.4V12l3 2"/>',
  /** Tagesblatt. */
  tag: '<rect x="4" y="4.6" width="16" height="15.4" rx="2.4"/><path d="M4 9.4h16M8.6 3v3.2M15.4 3v3.2"/>',
  aufnahme: '<path d="M8.6 5.4v13.2L18.4 12z"/>',
  /** Leeres Kästchen — steht neben dem Haken-KNOPF, darf also kein Haken sein. */
  aufgabe: '<rect x="4.4" y="4.4" width="15.2" height="15.2" rx="3.4"/>',

  /*
   * ── Geschlecht an der Stimmen-Karte (15.09.2026)
   *
   * ⚠️ Martin am 24.07.: *„Icon passt nicht."* Gemeint waren die Unicode-
   *    Zeichen ♀ und ♂ — iOS malt sie als BUNTE EMOJI. In den Einstellungen
   *    wurden sie damals durch gezeichnete Symbole ersetzt, im Willkommens-
   *    Schritt blieben sie stehen. Genau der Schritt, den ein neuer Mensch
   *    als Ersten sieht.
   *
   * ⚠️ Die Zeichnung stand seither INLINE in settings-view.ts. Sie gehoert
   *    hierher, sonst entsteht beim zweiten Verwender die Kopie, vor der
   *    der Kopf dieses Blattes warnt.
   */
  /*
   * ── Mikrofon (16.09.2026)
   *
   * ⚠️ Martin: *„Ich möchte, dass das Mikrofonzeichen irgendwie logischer ist.
   *    Es ist so komisch rot und unschön dargestellt."*
   *
   *    Nachgezählt: die App zeichnete ihr Mikrofon an drei Stellen mit DREI
   *    verschiedenen Strichstärken (2 / 1.7 / gefüllt) — keine davon die 1.6
   *    dieses Vorrats. Hier ist es einmal, im Stil aller anderen Zeichen.
   */
  /*
   * ── Wunsch (16.09.2026)
   *
   * Martin: *„Ein weiteres Symbol, auf das er draufklicken kann, um seine
   * Wuensche zu aeussern. […] Das kann man gerne auch deutlicher sichtbar
   * machen, so als Einladung, faktisch."*
   *
   * Eine Sprechblase mit einem Funken darin: es wird gesprochen, und es geht
   * um eine Idee. Kein Werkzeug, keine Gluehbirne — die truege eine fremde
   * Bildsprache in ein Blatt voller Strich-Zeichen.
   */
  wunsch:
    '<path d="M5 5.2h14a2 2 0 0 1 2 2v7.4a2 2 0 0 1-2 2h-6.3L8.6 20v-3.4H5a2 2 0 0 1-2-2V7.2a2 2 0 0 1 2-2Z"/><path d="M12 8.2l1.1 2.2 2.2 1.1-2.2 1.1L12 14.8l-1.1-2.2-2.2-1.1 2.2-1.1z"/>',

  mikro:
    '<rect x="9.2" y="3.2" width="5.6" height="10.6" rx="2.8"/><path d="M5.8 11.4a6.2 6.2 0 0 0 12.4 0"/><path d="M12 17.6v3.2"/>',
  // Mute und Ton aus (29.10.2026): dasselbe Mikro bzw. derselbe Lautsprecher, aber durchgestrichen.
  'mikro-aus':
    '<rect x="9.2" y="3.2" width="5.6" height="10.6" rx="2.8"/><path d="M5.8 11.4a6.2 6.2 0 0 0 12.4 0"/><path d="M12 17.6v3.2"/><path d="M4 4l16 16"/>',
  lautsprecher:
    '<path d="M4.6 9.6h3L12 5.8v12.4l-4.4-3.8h-3z"/><path d="M15.6 9.4a3.8 3.8 0 0 1 0 5.2M18 7.2a7 7 0 0 1 0 9.6"/>',
  'ton-aus':
    '<path d="M4.6 9.6h3L12 5.8v12.4l-4.4-3.8h-3z"/><path d="M15.4 9.6l4.8 4.8M20.2 9.6l-4.8 4.8"/>',

  weiblich: '<circle cx="12" cy="8.6" r="4.8"/><path d="M12 13.4v7.2M9.2 17.8h5.6"/>',
  maennlich: '<circle cx="10.2" cy="13.8" r="4.8"/><path d="m13.8 10.2 6.2-6.2M15.2 4h4.8v4.8"/>',

  /**
   * Der Weg zurueck nach Zuhause (29.08.).
   *
   * Martin: „Wenn ich im Chat bin, dann komm ich zum Zuhause nicht mehr mit
   * einem Button." Der Weg gab es schon — das Markenzeichen links oben ruft
   * `goToView("zuhause")` — aber es war 32x30 Punkte gross und sah aus wie
   * eine Marke, nicht wie ein Knopf. Ein Daumen braucht 44x44.
   */
  haus: HAUS,

  // ── Kontakt-Kategorien (26.08., vorher Emoji)
  familie: HAUS,

  freunde:
    '<circle cx="8.4" cy="8.6" r="3"/><circle cx="16.4" cy="9.6" r="2.4"/>' +
    '<path d="M3 19.4c.8-3 2.8-4.6 5.4-4.6s4.6 1.6 5.4 4.6"/><path d="M15 14.9c2.2.1 3.8 1.6 4.4 4.5"/>',
  arbeit:
    '<rect x="3.4" y="7.4" width="17.2" height="12.2" rx="2.2"/>' +
    '<path d="M9 7.4V5.8a1.6 1.6 0 0 1 1.6-1.6h2.8A1.6 1.6 0 0 1 15 5.8v1.6"/><path d="M3.4 12.6h17.2"/>',
  dienste:
    '<path d="M15.6 3.6a5 5 0 0 0-5.9 6.6L3.8 16a2 2 0 0 0 2.8 2.8l5.8-5.9a5 5 0 0 0 6.6-5.9l-3 3-2.4-2.4Z"/>',
  /** Kreuz im Kreis — Notfall, ohne die Farbigkeit eines Rettungswagen-Emoji. */
  notfall: '<circle cx="12" cy="12" r="8.6"/><path d="M12 7.8v8.4M7.8 12h8.4"/>',

  // ── Allgemein (26.08., vorher Dingbats)
  /*
   * Sprechblase — der Weg von Zuhause in den Chat (29.08.). Gegenstueck zum
   * `haus`, das im Chat zurueckfuehrt: zwei Knoepfe an derselben Stelle, ein
   * Hin und Her. Deshalb derselbe Strich, dieselbe Groesse.
   */
  gespraech:
    '<path d="M20.4 12.4c0 3.9-3.8 7-8.4 7-1 0-2-.15-2.9-.43L4 20.4l1.5-3.7C4.6 15.5 3.6 14 3.6 12.4c0-3.9 3.8-7 8.4-7s8.4 3.1 8.4 7Z"/>',
  /*
   * Telefonhörer (06.10.2026) — die Karte „Vespera ruft an“ (anruf-karte-
   * ansicht.ts). Derselbe Pfad wie der Anruf-Knopf in der Fußleiste
   * (index.html, #dock-connect), damit Anrufen und Abheben gleich aussehen.
   * Um 135° gedreht (CSS) ist er „Ablehnen“.
   */
  hoerer:
    '<path d="M6.4 4.1c.9 0 1.7.6 1.9 1.5l.6 2.6c.2.8-.1 1.6-.8 2.1l-1.1.7c1 2 2.6 3.6 4.6 4.6l.7-1.1c.5-.7 1.3-1 2.1-.8l2.6.6c.9.2 1.5 1 1.5 1.9v2.1c0 1.1-.9 2-2 1.9C10.3 19.6 4.4 13.7 3.6 6.1c-.1-1.1.8-2 1.9-2Z"/>',
  haken: '<path d="M4.8 12.4 9.6 17.2 19.2 7"/>',
  daumen: '<path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>',
  /** Daumen (Witz des Tages): hoch = gut, runter = dieselbe Hand um 180° gedreht. */
  "daumen-hoch":
    '<path d="M7 11v8.6H4.4V11H7Z"/><path d="m7 11 3.6-6.6c1.6 0 2.6 1.2 2.2 2.8L12.2 10h5.6a2 2 0 0 1 2 2.4l-1.2 5.8a2 2 0 0 1-2 1.6H7"/>',
  "daumen-runter":
    '<g transform="rotate(180 12 12)"><path d="M7 11v8.6H4.4V11H7Z"/><path d="m7 11 3.6-6.6c1.6 0 2.6 1.2 2.2 2.8L12.2 10h5.6a2 2 0 0 1 2 2.4l-1.2 5.8a2 2 0 0 1-2 1.6H7"/></g>',
  schliessen: '<path d="M6.2 6.2 17.8 17.8M17.8 6.2 6.2 17.8"/>',
  /* ── Vespera 9.0 (N2, 29.10.2026): die Zeichen der Gesprächs-Seite /neun. */
  /** Einstellungen — ein Zahnrad aus Kreis und acht Zähnen. */
  einstellungen:
    '<circle cx="12" cy="12" r="3"/><path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6 6l1.6 1.6M16.4 16.4 18 18M6 18l1.6-1.6M16.4 7.6 18 6"/>',
  /** Schreiben — ein Stift. */
  schreiben: '<path d="m5 19 1-4L16.5 4.5a2 2 0 0 1 3 3L9 18Z"/><path d="m14.5 6.5 3 3"/>',
  /** Anhang — die Büroklammer. */
  anhang: '<path d="m19 11.5-6.8 6.8a4 4 0 0 1-5.7-5.7l7-7a2.7 2.7 0 0 1 3.8 3.8l-7 7a1.3 1.3 0 0 1-1.9-1.9l6.5-6.5"/>',
  /** Nachsehen — die Weltkugel: Vespera schaut im Netz nach. */
  nachsehen:
    '<circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17M12 3.5c2.5 2.5 3.5 5.3 3.5 8.5s-1 6-3.5 8.5c-2.5-2.5-3.5-5.3-3.5-8.5s1-6 3.5-8.5Z"/>',
  /** Klang — Wellenform statt Notenschluessel: sagt "Aufnahme", nicht "Musik". */
  klang: '<path d="M4 12h2.4M8.4 7.6v8.8M12 4.6v14.8M15.6 8.6v6.8M19.2 11h1.2"/>',
  dokument:
    '<path d="M6.4 3.6h7.2L18.6 8.6v11.8H6.4Z"/><path d="M13.4 3.8v5h5"/><path d="M9.2 13.4h5.6M9.2 16.6h5.6"/>',

  // ── Einstellungen-Übersicht (23.09., Entwurf „Einstellungen neu geordnet")
  /** Persönlichkeit — ein Stern mit fünf Zacken (der `stern` ist eine Sonne). */
  persona:
    '<path d="M12 3.5l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4-3.9-3.8 5.4-.8z"/>',
  /** Tagesaufnahme — der Aufnahmepunkt, nicht das Abspiel-Dreieck. */
  aufzeichnen: '<circle cx="12" cy="12" r="7.5"/><circle cx="12" cy="12" r="3" fill="currentColor"/>',
  /** Stimmprobe — Kopf und Schultern: die eine Stimme, die „du" ist. */
  person: '<circle cx="12" cy="8" r="3.5"/><path d="M5 20c1.2-3.6 4-5.5 7-5.5s5.8 1.9 7 5.5"/>',
  /** Eigene Wörter — Zeilen und ein Pfeil: Wörter, die hineinwandern. */
  woerter: '<path d="M4 6h10M4 12h7M4 18h9M16 14l4 4-4 4"/>',
  /** Weiter — der Pfeil am Ende einer Zeile, die auf eine Seite führt. */
  "pfeil-rechts": '<path d="m9.5 6.5 5.5 5.5-5.5 5.5"/>',
  /** Zurück — „← Menü" oben in der Einstellungsleiste am Rechner. */
  "pfeil-links": '<path d="m14.5 6.5-5.5 5.5 5.5 5.5"/>',
  /**
   * Teilen (Ticket 2892; 19.10.2026 neu gezeichnet): drei verbundene Punkte — deutlich anders als Herunterladen
   * (Pfeil nach unten in die Schale), vorher zum Verwechseln aehnlich (Pfeil nach oben aus der Ablage). Bewusst KEIN Vorwaerts-Pfeil: „Weiterleiten“ innerhalb von Vespera gibt es nicht.
   */
  teilen:
    '<circle cx="17.6" cy="5.8" r="2.4"/><circle cx="6.4" cy="12" r="2.4"/><circle cx="17.6" cy="18.2" r="2.4"/><path d="m8.5 10.8 7-3.8M8.5 13.2l7 3.8"/>',
  /** Herunterladen: Pfeil nach unten in die Ablage. */
  herunterladen: '<path d="M12 4v11M8 11.4l4 4 4-4"/><path d="M5.4 12.6v6.2a1.6 1.6 0 0 0 1.6 1.6h10a1.6 1.6 0 0 0 1.6-1.6v-6.2"/>',
  /** Papierflieger — die Kanalmarke von Telegram in der Zeitzeile einer Blase (Ticket 2892). */
  telegram: '<path d="M20.6 3.8 3.4 10.6l6.6 2.4 2.4 6.6z"/><path d="m10 13 4.8-4.8"/>',
  /** Bild — Kachel einer Werkzeugkarte (Ticket 2892). */
  bild: '<rect x="3.4" y="4.6" width="17.2" height="14.8" rx="2.6"/><path d="m3.6 15.4 4.8-4.8 4.4 4.4 2.8-2.8 4.8 4.8"/><circle cx="16.4" cy="8.8" r="1.3"/>',
  /** Hinzufügen. */
  plus: '<path d="M12 5v14M5 12h14"/>',
  /** Weniger, kleiner (Zoom der PDF-Ansicht). */
  minus: '<path d="M5 12h14"/>',
  /** Suchen. */
  suche: '<circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/>',

  // ── Kopf und Zustaende (seit 26.08., Norm A: keine Dingbats)
  /** Mehr-Menue. Ersetzt ⋯ (U+22EF), das als Schriftzeichen mit der
      Schriftgroesse sprang und in jeder Schrift anders breit war. */
  mehr: '<circle cx="5.2" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="18.8" cy="12" r="1.5" fill="currentColor" stroke="none"/>',
  /** Aufklappen. Ersetzt ▾ (U+25BE). */
  "pfeil-ab": '<path d="m6.5 9.5 5.5 5.5 5.5-5.5"/>',
  /** Zuklappen. Ersetzt ▴ (U+25B4). */
  "pfeil-auf": '<path d="m6.5 14.5 5.5-5.5 5.5 5.5"/>',
  /** Abspielen. Ersetzt ▶ (U+25B6). */
  abspielen: '<path d="M8.4 5.6 19 12 8.4 18.4Z" fill="currentColor" stroke="none"/>',
  /** Pause: zwei Striche (Vespera hält an). */
  pause: '<path d="M9 5.8v12.4M15 5.8v12.4"/>',
  /** Stopp: ein gefülltes Quadrat (die Antwort endet, das Mikrofon ist frei). Das Gegenstück zu `abspielen`. */
  stopp: '<rect x="6.4" y="6.4" width="11.2" height="11.2" rx="2" fill="currentColor" stroke="none"/>',
  /** Laeuft gerade. Ersetzt ● (U+25CF). */
  laeuft: '<circle cx="12" cy="12" r="4.4" fill="currentColor" stroke="none"/>',
  /** Fehlgeschlagen. Ersetzt ✕ (U+2715). */
  fehler: '<path d="m7 7 10 10M17 7 7 17"/>',
  /** Mond. Ersetzt die Mondphasen-Emoji (U+1F311…U+1F318). */
  mond: '<path d="M20 13.4A8.4 8.4 0 1 1 10.6 4a6.6 6.6 0 0 0 9.4 9.4Z"/>',
  /** Neu laden — Kreispfeil. CSS-Mask auf dem iPhone blieb oft leer. */
  laden:
    '<path d="M20 6.4v5.2h-5.2"/><path d="M18.2 14.2A6.4 6.4 0 1 1 17 8.4L20 11.6"/>',
  /** Was ist neu — ein Funke mit seinem kleinen Begleiter (05.10.2026, Ticket 2931).
      EINE Zeichnung für den Knopf am Fuß des Menüs und den Titel des Fensters. Offen
      gezeichnet, nicht gefüllt: gefüllt und mit Verlauf ist der Stern die MARKE in der
      Kopfleiste.
      ACHTUNG: Der Begleiter ist der ZWEITE Pfad und sitzt bei (19,2 | 5,2): in der
      Symbolleiste tritt an seine Stelle der Punkt, wenn etwas ungesehen ist
      (style.css, body.nav-rail .nav-neu). */
  neu: '<path d="M10.6 4.8c.7 5.2 3 7.5 8.2 8.2-5.2.7-7.5 3-8.2 8.2-.7-5.2-3-7.5-8.2-8.2 5.2-.7 7.5-3 8.2-8.2Z"/><path d="M19.2 3v4.4M17 5.2h4.4"/>',
  /** Akku / Erholung. Ersetzt 🔋 (U+1F50B). */
  akku: '<rect x="3" y="7.6" width="15.4" height="8.8" rx="2.2"/><path d="M21 10.6v2.8"/>',
  /** Human Design / Kosmos. Ersetzt ein handgeschriebenes Sonnen-SVG
      (gefuellt, ohne Strich) in der Nutzerverwaltung — es war das einzige
      Zeichen der App mit fremder Machart. */
  stern: '<circle cx="12" cy="12" r="4"/><path d="M12 2.6v2.8M12 18.6v2.8M2.6 12h2.8M18.6 12h2.8M5.4 5.4l2 2M16.6 16.6l2 2M18.6 5.4l-2 2M7.4 16.6l-2 2"/>',

  /* ── DIE SECHS WEGE (27.09.2026, Ticket 2636) ────────────────────────────
     Der freigegebene Entwurf B („eine Tafel") gibt jedem Weg ein feines
     Zeichen im Ring. Pfade 1:1 aus dem Entwurf; die Strichstärke kommt wie
     bei allen aus zeichen() (1,6 statt 1,5 im Entwurf — EIN Stil). */
  /** Spiegel — ein Handspiegel auf seinem Fuß. */
  "weg-spiegel": '<ellipse cx="12" cy="9.5" rx="5.5" ry="6.5"/><path d="M12 16v5M9 21h6"/>',
  /** Frage — das Fragezeichen im Kreis. */
  "weg-frage":
    '<circle cx="12" cy="12" r="8.5"/><path d="M9.6 9.6a2.4 2.4 0 1 1 3.2 2.3c-.5.2-.8.6-.8 1.1v.8"/><path d="M12 16.6v.2"/>',
  /** Tugend — ein Blatt, das wächst. */
  "weg-tugend": '<path d="M5 19c0-8 5-13 14-14-1 9-6 14-14 14z"/><path d="M5 19l7-7"/>',
  /** Impuls — ein Funke, der nach allen Seiten geht. */
  "weg-impuls":
    '<path d="M12 3.5v4M12 16.5v4M3.5 12h4M16.5 12h4M6.6 6.6l2.6 2.6M14.8 14.8l2.6 2.6M6.6 17.4l2.6-2.6M14.8 9.2l2.6-2.6"/>',
  /** Aufgaben — eine Liste, zwei Zeilen abgehakt. */
  "weg-aufgaben":
    '<path d="M10 6.5h10M10 12h10M10 17.5h10"/><path d="M4 6.5l1.2 1.2L7.5 5.4M4 12l1.2 1.2 2.3-2.3"/><circle cx="5.6" cy="17.5" r="1"/>',
  /** Meditation — die Lotusblüte. */
  "weg-meditation":
    '<path d="M12 19.5c-4 0-7-2-8-5 3 0 6 1 8 3 2-2 5-3 8-3-1 3-4 5-8 5z"/><path d="M12 17.5c-1.8-2-2.6-5-1.8-8.5 1 .8 1.8 1.8 1.8 2.8 0-1 .8-2 1.8-2.8.8 3.5 0 6.5-1.8 8.5z"/>',

  /* ── DIE TUGEND-FAMILIEN (02.09.2026) ────────────────────────────────────
     Martin: *„Vielleicht dass jede Tugend auch ein Symbol bekommt."*

     ACHTUNG: Zehn Zeichen, nicht hunderteins. Es gibt 101 Tugenden in 10 Familien
     (shared/tugenden.ts). Für jede einzelne ein eigenes Zeichen zu erfinden
     hiesse, 101-mal etwas zu zeichnen, das niemand unterscheiden kann — und
     einen Vorrat zu pflegen, der groesser ist als der Rest der App zusammen.
     Die FAMILIE ist die Ebene, auf der ein Zeichen noch etwas sagt: Mut
     sieht anders aus als Gelassenheit, aber Mut und Standhaftigkeit gehoeren
     zusammen.

     Alle im selben Stil wie der Rest: 24er-Raster, Strichstaerke 1.6,
     currentColor, runde Enden. Keine Emoji — siehe Kopf dieser Datei. */

  /** Weisheit & Erkenntnis — ein Auge, das offen ist. */
  "tugend-weisheit":
    '<path d="M2.6 12s3.4-5.6 9.4-5.6S21.4 12 21.4 12s-3.4 5.6-9.4 5.6S2.6 12 2.6 12Z"/><circle cx="12" cy="12" r="2.6"/>',
  /** Mut & innere Staerke — die Flamme, die nicht ausgeht. */
  "tugend-mut":
    '<path d="M12 3.4c3.4 3.6 5.4 6.2 5.4 9a5.4 5.4 0 0 1-10.8 0c0-2.8 2-5.4 5.4-9Z"/><path d="M12 19.2a2.6 2.6 0 0 1-2.4-3.6c.5-1.2 1.4-2 2.4-3 1 1 1.9 1.8 2.4 3a2.6 2.6 0 0 1-2.4 3.6Z"/>',
  /** Integritaet & Wahrhaftigkeit — das Lot, das senkrecht faellt. */
  "tugend-integritaet":
    '<path d="M12 3v11.4"/><path d="M8.4 14.4h7.2L12 20.6Z"/><path d="M6.6 3h10.8"/>',
  /** Mitgefuehl & Menschlichkeit — die offene Hand, die traegt. */
  "tugend-mitgefuehl":
    '<path d="M12 20.4s-6.6-4.2-6.6-9a3.9 3.9 0 0 1 6.6-2.8 3.9 3.9 0 0 1 6.6 2.8c0 4.8-6.6 9-6.6 9Z"/>',
  /** Gerechtigkeit & Gemeinschaft — die Waage im Gleichgewicht. */
  "tugend-gerechtigkeit":
    '<path d="M12 4v16M6 20h12M4.4 8.6h15.2"/><path d="M4.4 8.6 2 13.4h4.8ZM19.6 8.6 17.2 13.4H22Z"/>',
  /** Emotionale Qualitaeten — die Welle, die steigt und faellt. */
  "tugend-emotion":
    '<path d="M2.6 14.2c2.4 0 2.4-4.4 4.8-4.4s2.4 4.4 4.8 4.4 2.4-4.4 4.8-4.4 2.4 4.4 4.4 4.4"/><path d="M2.6 18.4c2.4 0 2.4-2.4 4.8-2.4s2.4 2.4 4.8 2.4 2.4-2.4 4.8-2.4 2.4 2.4 4.4 2.4" opacity="0.5"/>',
  /** Selbstfuehrung & Reife — der Kompass mit fester Nadel. */
  "tugend-selbstfuehrung":
    '<circle cx="12" cy="12" r="8.6"/><path d="m15.2 8.8-2 5.2-5.2 2 2-5.2Z"/>',
  /** Sozialkompetenz & Fuehrung — zwei, die zueinander stehen. */
  "tugend-sozial":
    '<circle cx="8.6" cy="8.4" r="2.8"/><circle cx="16" cy="9.4" r="2.4"/><path d="M3.4 19.4c.8-3 2.8-4.6 5.2-4.6s4.4 1.6 5.2 4.6"/><path d="M15 14.8c2.2.1 3.8 1.6 4.4 4.6"/>',
  /** Innerer Frieden & Gelassenheit — Kreise, die zur Ruhe kommen. */
  "tugend-frieden":
    '<circle cx="12" cy="12" r="8.6"/><circle cx="12" cy="12" r="4.8" opacity="0.6"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/>',
  /** Transzendenz & Sinn — der Weg, der ueber sich hinausweist. */
  "tugend-sinn":
    '<path d="M12 21V9.4"/><path d="M12 9.4 7.4 4.8M12 9.4l4.6-4.6"/><circle cx="12" cy="6" r="1.4" fill="currentColor" stroke="none"/><path d="M8.6 21h6.8"/>',
};

/**
 * Welches Zeichen gehoert zu welcher Tugend-Familie?
 *
 * ACHTUNG: Der Schluessel ist die Kategorie, WOERTLICH wie in shared/tugenden.ts.
 * Kein Teiltreffer, keine Kleinschreibung: eine ungefaehre Zuordnung faellt
 * still auf das falsche Zeichen zurueck, und niemand merkt es. Was hier nicht
 * steht, bekommt den Stern — sichtbar neutral, statt falsch.
 */
const TUGEND_ZEICHEN: Record<string, string> = {
  "Weisheit & Erkenntnis": "tugend-weisheit",
  "Mut & innere Stärke": "tugend-mut",
  "Integrität & Wahrhaftigkeit": "tugend-integritaet",
  "Mitgefühl & Menschlichkeit": "tugend-mitgefuehl",
  "Gerechtigkeit & Gemeinschaft": "tugend-gerechtigkeit",
  "Emotionale Qualitäten": "tugend-emotion",
  "Selbstführung & Reife": "tugend-selbstfuehrung",
  "Sozialkompetenz & Führung": "tugend-sozial",
  "Innerer Frieden & Gelassenheit": "tugend-frieden",
  "Transzendenz & Sinn": "tugend-sinn",
};

/** Das Zeichen einer Tugend-Familie — `stern`, wenn die Familie unbekannt ist. */
export function tugendZeichenName(kategorie: string): string {
  return TUGEND_ZEICHEN[kategorie] ?? "stern";
}

/** Alle Familien, die ein eigenes Zeichen haben — fuer Tests. */
export function tugendFamilien(): string[] {
  return Object.keys(TUGEND_ZEICHEN);
}

/**
 * Welches Zeichen gehört zu welchem Weg der Tafel? (27.09.2026, Ticket 2636)
 *
 * ⚠️ Der Schlüssel ist der SCHLÜSSEL des Servers (server/tagesbild.ts), nicht
 *    das Wort: `heute` heißt auf der Seite „Impuls", `erinnerung` heißt
 *    „Aufgaben". Was hier fehlt, bekommt den Stern — sichtbar neutral.
 */
const WEG_ZEICHEN: Record<string, string> = {
  spiegel: "weg-spiegel",
  frage: "weg-frage",
  tugend: "weg-tugend",
  heute: "weg-impuls",
  erinnerung: "weg-aufgaben",
  meditation: "weg-meditation",
};

/** Das Zeichen eines Wegs — `stern`, wenn der Schlüssel unbekannt ist. */
export function wegZeichenName(schluessel: string): string {
  return WEG_ZEICHEN[schluessel] ?? "stern";
}

/**
 * Ein gezeichnetes Zeichen als SVG-Markup.
 *
 * @param art   Name aus dem Vorrat. Unbekanntes bekommt das Aufgaben-Kästchen,
 *              damit eine Zeile nie ohne Zeichen dasteht.
 * @param groesse Kantenlänge in Punkten (Standard 20).
 */
export function zeichen(art: string, groesse = 20): string {
  return huelle(PFADE[art] ?? PFADE.aufgabe, groesse);
}

/** Die gemeinsame Hülle aller Zeichen: 24er-Raster, Strichstärke 1,6, Textfarbe, runde Enden. */
function huelle(d: string, groesse: number): string {
  return (
    `<svg class="zeichen" viewBox="0 0 24 24" width="${groesse}" ` +
    `height="${groesse}" aria-hidden="true" fill="none" stroke="currentColor" ` +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
    d +
    "</svg>"
  );
}

/*
 * ── Zustand einer Arbeit (3.11.2026, Fortschrittskarte im Chat, lib/entwicklung-karte.ts)
 *
 * Drei FORMEN, damit der Zustand nie nur an der Farbe hängt: voller Kreis mit Haken = erledigt, Ring mit Bogen = läuft,
 * gestrichelter Ring = offen. Die Klassen (`zeichen-…`) sind Haken für das Blatt der Karte: der Haken nimmt dort die Kartenfarbe
 * an, der Bogen dreht sich, und mit „Bewegung reduzieren“ tritt an seine Stelle ein ruhiger Kern im Ring.
 *
 * ⚠️ Eigene Tabelle statt Einträge in PFADE: PFADE steckt als Ganzes in jedem Bündel (auch /neun und Playground, deren Größe der
 *    Qualitäts-Wächter zählt). Diese Tabelle ist nur hier und in der Karte gebraucht — wer sie nicht einführt, bekommt sie nicht
 *    (Tree-Shaking), und die Bündel wachsen nicht.
 */
export const ZUSTAND_PFADE = {
  /** Erledigt: voller Kreis, der Haken darin ist ausgespart (CSS färbt `.zeichen-aus` in die Kartenfarbe). */
  erledigt: '<circle cx="12" cy="12" r="8.6" fill="currentColor"/><path class="zeichen-aus" d="m8.2 12.4 2.7 2.7 5-5.6"/>',
  /** Läuft: Spur und Viertelbogen (der dreht sich); der Kern ist nur für „Bewegung reduzieren“ da. */
  laeuft:
    '<circle class="zeichen-spur" cx="12" cy="12" r="8.4"/><path class="zeichen-bogen" d="M12 3.6a8.4 8.4 0 0 1 8.4 8.4"/><circle class="zeichen-kern" cx="12" cy="12" r="2.6" fill="currentColor" stroke="none"/>',
  /** Offen: gestrichelter Ring, acht Striche (Umfang 52,8 = 8 mal 6,6; die runden Enden machen aus 1,4 Strich und 5,2 Spalt 3,0 und 3,6). */
  offen: '<circle cx="12" cy="12" r="8.4" stroke-dasharray="1.4 5.2"/>',
} as const;
export type ZustandName = keyof typeof ZUSTAND_PFADE;

/** Ein Zustands-Zeichen als SVG-Markup (gleiche Hülle wie `zeichen`). */
export function zustandZeichen(zustand: ZustandName, groesse = 16): string {
  return huelle(ZUSTAND_PFADE[zustand], groesse);
}

/** Alle Namen des Vorrats — für Tests und Übersichten. */
export function zeichenNamen(): string[] {
  return Object.keys(PFADE);
}
